import { create } from 'zustand';
import { usePatientsStore } from './patients';
import { useDoctorsStore } from './doctors';
import { useBillsStore } from './bills';
import { format, differenceInDays, differenceInMinutes } from 'date-fns';
import type { Patient } from '@/types';

interface AnalyticsState {
  getRevenueSummary: () => {
    totalRevenue: number;
    doctorRevenue: { doctorId: string; doctorName: string; revenue: number }[];
    dateRevenue: { date: string; revenue: number }[];
    paymentMethodRevenue: { method: string; revenue: number }[];
    serviceRevenue: { serviceName: string; revenue: number; count: number }[];
    presentMonthServiceRevenue: { serviceName: string; revenue: number; count: number }[];
    previousMonthServiceRevenue: { serviceName: string; revenue: number; count: number }[];
    monthlyTrend: { month: string; revenue: number; patients: number }[];
    revenueForecast: { date: string; forecast: number }[];
    dailyRevenueData: { day: string; currentMonth: number; previousMonth: number }[];
    dailyPatientData: { day: string; currentMonth: number; previousMonth: number }[];
    growthMetrics: {
      revenueGrowth: number;
      patientGrowth: number;
      avgRevenuePerPatient: number;
    };
    retentionMetrics: {
      returningPatients: number;
      retentionRate: number;
      avgDaysBetweenVisits: number;
    };
    doctorKPIs: {
      doctorId: string;
      doctorName: string;
      avgRevenuePerPatient: number;
      patientSatisfaction: number;
      efficiency: number;
    }[];
    timeAnalytics: {
      peakHours: { hour: number; count: number }[];
      peakDays: { day: string; count: number }[];
      avgWaitTime: number;
    };
  };
  getPatientCountByDoctor: () => { doctorId: string; doctorName: string; count: number }[];
  getCompletedAppointmentsByDate: (startDate?: Date, endDate?: Date) => {
    date: string;
    count: number;
  }[];
}

export const useAnalyticsStore = create<AnalyticsState>()((get) => ({
  getRevenueSummary: () => {
    try {
      const { patients } = usePatientsStore.getState();
      const { doctors } = useDoctorsStore.getState();
      const { bills } = useBillsStore.getState();
      
      const completedPatients = patients.filter(p => p.status === "completed");
      
      // Helper function to calculate revenue from paymentHistory (preserved and never cleared)
      // paymentHistory is the source of truth for revenue as it's never reset after bill generation
      const getPatientRevenue = (patient: Patient) => {
        // Use paymentHistory as primary source (preserved even after bill generation)
        if (patient.paymentHistory && patient.paymentHistory.length > 0) {
          const revenue = patient.paymentHistory.reduce((sum: number, payment) => sum + (Number(payment.amount) || 0), 0);
          return revenue;
        }
        
        // Fallback: Check bills if paymentHistory is not available (for legacy data)
        const patientBills = bills.filter(b => b.patientId === patient.id && b.status === 'active');
        if (patientBills.length > 0) {
          const revenue = patientBills.reduce((sum, bill) => sum + (Number(bill.amountPaid) || 0), 0);
          return revenue;
        }
        
        // Final fallback to amountPaid for backward compatibility (should rarely be used)
        return patient.amountPaid || 0;
      };
      
      // Get current and previous month dates
      const currentDate = new Date();
      const currentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const previousMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
      
      // Calculate total revenue using paymentHistory (not amountPaid which gets reset)
      const totalRevenue = completedPatients.reduce((sum, p) => sum + getPatientRevenue(p), 0);
      console.log('Analytics: Total revenue:', totalRevenue);
      
      // Calculate revenue by doctor
      const doctorRevenue = doctors.map(doctor => {
        const doctorPatients = completedPatients.filter(p => p.doctorId === doctor.id);
        const revenue = doctorPatients.reduce((sum, p) => sum + getPatientRevenue(p), 0);
        return {
          doctorId: doctor.id,
          doctorName: doctor.name,
          revenue,
        };
      }).sort((a, b) => b.revenue - a.revenue);
      
      console.log('Analytics: Doctor revenue:', doctorRevenue);
      
      // Calculate revenue by date - use paymentHistory dates (primary source)
      const dateMap = new Map<string, number>();
      completedPatients.forEach(patient => {
        const patientRevenue = getPatientRevenue(patient);
        
        // Use paymentHistory dates as primary source (preserved and never cleared)
        if (patient.paymentHistory && patient.paymentHistory.length > 0) {
          patient.paymentHistory.forEach((payment) => {
            const paymentDate = payment.date ? new Date(payment.date) : (patient.completedAt ? new Date(patient.completedAt) : new Date(patient.registeredAt));
            const date = paymentDate.toISOString().split('T')[0];
            const currentRevenue = dateMap.get(date) || 0;
            dateMap.set(date, currentRevenue + (Number(payment.amount) || 0));
          });
        } else {
          // Fallback: Check bills if paymentHistory is not available
          const patientBills = bills.filter(b => b.patientId === patient.id && b.status === 'active');
          if (patientBills.length > 0) {
            patientBills.forEach(bill => {
              const billDate = bill.billDate ? new Date(bill.billDate) : (bill.createdAt ? new Date(bill.createdAt) : (patient.completedAt ? new Date(patient.completedAt) : new Date(patient.registeredAt)));
              const date = billDate.toISOString().split('T')[0];
              const currentRevenue = dateMap.get(date) || 0;
              dateMap.set(date, currentRevenue + (Number(bill.amountPaid) || 0));
            });
          } else {
            // Final fallback: use completedAt or registeredAt for date
            const paymentDate = patient.completedAt || patient.registeredAt;
            if (paymentDate) {
              const date = new Date(paymentDate).toISOString().split('T')[0];
              const currentRevenue = dateMap.get(date) || 0;
              dateMap.set(date, currentRevenue + patientRevenue);
            }
          }
        }
      });
      
      const dateRevenue = Array.from(dateMap.entries())
        .map(([date, revenue]) => ({ date, revenue }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

      // Calculate revenue by payment method - use paymentHistory (primary source)
      const paymentMethodMap = new Map<string, number>();
      completedPatients.forEach(patient => {
        const patientRevenue = getPatientRevenue(patient);
        
        // Use paymentHistory as primary source (preserved and never cleared)
        if (patient.paymentHistory && patient.paymentHistory.length > 0) {
          patient.paymentHistory.forEach((payment) => {
            const method = payment.mode || patient.paymentMode || 'Unknown';
            const currentRevenue = paymentMethodMap.get(method) || 0;
            paymentMethodMap.set(method, currentRevenue + (Number(payment.amount) || 0));
          });
        } else {
          // Fallback: Check bills if paymentHistory is not available
          const patientBills = bills.filter(b => b.patientId === patient.id && b.status === 'active');
          if (patientBills.length > 0) {
            patientBills.forEach(bill => {
              const method = bill.paymentMode || 'Unknown';
              const currentRevenue = paymentMethodMap.get(method) || 0;
              paymentMethodMap.set(method, currentRevenue + (Number(bill.amountPaid) || 0));
            });
          } else {
            // Final fallback: use patient's paymentMode
            const method = patient.paymentMode || 'Unknown';
            const currentRevenue = paymentMethodMap.get(method) || 0;
            paymentMethodMap.set(method, currentRevenue + patientRevenue);
          }
        }
      });

      const paymentMethodRevenue = Array.from(paymentMethodMap.entries())
        .map(([method, revenue]) => ({ method, revenue }))
        .sort((a, b) => b.revenue - a.revenue);

      // Calculate revenue by service type (both present and previous month)
      const presentMonthServiceMap = new Map<string, { revenue: number; count: number }>();
      const previousMonthServiceMap = new Map<string, { revenue: number; count: number }>();
      
      completedPatients.forEach(patient => {
        if (patient.registeredAt) {
          const patientDate = new Date(patient.registeredAt);
          const isPresentMonth = patientDate.getMonth() === currentMonth.getMonth() && 
                                patientDate.getFullYear() === currentMonth.getFullYear();
          const isPreviousMonth = patientDate.getMonth() === previousMonth.getMonth() && 
                                 patientDate.getFullYear() === previousMonth.getFullYear();
          
          if (isPresentMonth || isPreviousMonth) {
            const targetMap = isPresentMonth ? presentMonthServiceMap : previousMonthServiceMap;
            
            if (patient.services && patient.services.length > 0) {
              patient.services.forEach(service => {
                const current = targetMap.get(service.service_name) || { revenue: 0, count: 0 };
                targetMap.set(service.service_name, {
                  revenue: current.revenue + service.price,
                  count: current.count + 1
                });
              });
            } else {
              // If no services, use a default "General Consultation"
              const current = targetMap.get('General Consultation') || { revenue: 0, count: 0 };
              targetMap.set('General Consultation', {
                revenue: current.revenue + getPatientRevenue(patient),
                count: current.count + 1
              });
            }
          }
        }
      });

      const presentMonthServiceRevenue = Array.from(presentMonthServiceMap.entries())
        .map(([serviceName, data]) => ({ serviceName, ...data }))
        .sort((a, b) => b.revenue - a.revenue);

      const previousMonthServiceRevenue = Array.from(previousMonthServiceMap.entries())
        .map(([serviceName, data]) => ({ serviceName, ...data }))
        .sort((a, b) => b.revenue - a.revenue);

      // For backward compatibility, keep serviceRevenue as previous month
      const serviceRevenue = previousMonthServiceRevenue;

      // Calculate monthly trends
      const monthlyMap = new Map<string, { revenue: number; patients: number }>();
      completedPatients.forEach(patient => {
        if (patient.registeredAt) {
          const month = new Date(patient.registeredAt).toISOString().slice(0, 7);
          const current = monthlyMap.get(month) || { revenue: 0, patients: 0 };
          monthlyMap.set(month, {
            revenue: current.revenue + getPatientRevenue(patient),
            patients: current.patients + 1
          });
        }
      });

      const monthlyTrend = Array.from(monthlyMap.entries())
        .map(([month, data]) => ({
          month: new Date(month + '-01').toLocaleString('default', { month: 'short', year: 'numeric' }),
          ...data
        }))
        .sort((a, b) => new Date(b.month).getTime() - new Date(a.month).getTime());

      // Generate real daily revenue data from actual patient data
      const dailyRevenueMap = new Map<string, { currentMonth: number; previousMonth: number }>();
      
      // Initialize daily revenue map for the current month
      const daysInCurrentMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
      for (let day = 1; day <= daysInCurrentMonth; day++) {
        dailyRevenueMap.set(day.toString(), { currentMonth: 0, previousMonth: 0 });
      }
      
      // Process real patient data for daily revenue
      completedPatients.forEach(patient => {
        const patientRevenue = getPatientRevenue(patient);
        if (patient.registeredAt && patientRevenue > 0) {
          const patientDate = new Date(patient.registeredAt);
          const day = patientDate.getDate().toString();
          
          // Check if patient is from current month
          if (patientDate.getMonth() === currentDate.getMonth() && 
              patientDate.getFullYear() === currentDate.getFullYear()) {
            const current = dailyRevenueMap.get(day) || { currentMonth: 0, previousMonth: 0 };
            current.currentMonth += patientRevenue;
            dailyRevenueMap.set(day, current);
          }
          // Check if patient is from previous month
          else if (patientDate.getMonth() === previousMonth.getMonth() && 
                   patientDate.getFullYear() === previousMonth.getFullYear()) {
            const current = dailyRevenueMap.get(day) || { currentMonth: 0, previousMonth: 0 };
            current.previousMonth += patientRevenue;
            dailyRevenueMap.set(day, current);
          }
        }
      });
      
      // Convert map to array for chart data
      const dailyRevenueData = Array.from(dailyRevenueMap.entries())
        .map(([day, data]) => ({
          day,
          currentMonth: data.currentMonth,
          previousMonth: data.previousMonth
        }))
        .sort((a, b) => parseInt(a.day) - parseInt(b.day));

      // Generate daily patient data from actual patient data
      const dailyPatientMap = new Map<string, { currentMonth: number; previousMonth: number }>();
      
      // Initialize daily patient map for the current month
      for (let day = 1; day <= daysInCurrentMonth; day++) {
        dailyPatientMap.set(day.toString(), { currentMonth: 0, previousMonth: 0 });
      }
      
      // Process real patient data for daily patient counts
      completedPatients.forEach(patient => {
        if (patient.registeredAt) {
          const patientDate = new Date(patient.registeredAt);
          const day = patientDate.getDate().toString();
          
          // Check if patient is from current month
          if (patientDate.getMonth() === currentDate.getMonth() && 
              patientDate.getFullYear() === currentDate.getFullYear()) {
            const current = dailyPatientMap.get(day) || { currentMonth: 0, previousMonth: 0 };
            current.currentMonth += 1; // Count patients, not revenue
            dailyPatientMap.set(day, current);
          }
          // Check if patient is from previous month
          else if (patientDate.getMonth() === previousMonth.getMonth() && 
                   patientDate.getFullYear() === previousMonth.getFullYear()) {
            const current = dailyPatientMap.get(day) || { currentMonth: 0, previousMonth: 0 };
            current.previousMonth += 1; // Count patients, not revenue
            dailyPatientMap.set(day, current);
          }
        }
      });
      
      // Convert map to array for chart data
      const dailyPatientData = Array.from(dailyPatientMap.entries())
        .map(([day, data]) => ({
          day,
          currentMonth: data.currentMonth,
          previousMonth: data.previousMonth
        }))
        .sort((a, b) => parseInt(a.day) - parseInt(b.day));

      // Only generate sample data if no real data exists
      if (dailyRevenueData.every(item => item.currentMonth === 0 && item.previousMonth === 0)) {
        const sampleData = [];
        for (let day = 1; day <= daysInCurrentMonth; day++) {
          const baseRevenue = 2500;
          const randomFactor = 0.5 + Math.random() * 1;
          const dailyRevenue = Math.round(baseRevenue * randomFactor);
          sampleData.push({
            day: day.toString(),
            currentMonth: dailyRevenue,
            previousMonth: Math.round(dailyRevenue * 0.9) // Slightly lower for previous month
          });
        }
        // Replace the array instead of pushing
        dailyRevenueData.splice(0, dailyRevenueData.length, ...sampleData);
      }

      // Generate sample patient data if no real data exists
      if (dailyPatientData.every(item => item.currentMonth === 0 && item.previousMonth === 0)) {
        const samplePatientData = [];
        for (let day = 1; day <= daysInCurrentMonth; day++) {
          const basePatients = 8;
          const randomFactor = 0.5 + Math.random() * 1;
          const dailyPatients = Math.round(basePatients * randomFactor);
          samplePatientData.push({
            day: day.toString(),
            currentMonth: dailyPatients,
            previousMonth: Math.round(dailyPatients * 0.9) // Slightly lower for previous month
          });
        }
        // Replace the array instead of pushing
        dailyPatientData.splice(0, dailyPatientData.length, ...samplePatientData);
      }

      // Calculate growth metrics
      const lastMonth = monthlyTrend[0];
      const prevMonth = monthlyTrend[1];
      const revenueGrowth = prevMonth && lastMonth
        ? ((lastMonth.revenue - prevMonth.revenue) / prevMonth.revenue) * 100 
        : 0;
      const patientGrowth = prevMonth && lastMonth
        ? ((lastMonth.patients - prevMonth.patients) / prevMonth.patients) * 100 
        : 0;
      const avgRevenuePerPatient = completedPatients.length > 0 
        ? totalRevenue / completedPatients.length 
        : 0;

      // Calculate retention metrics
      // Use masterPatientId or contactNumber to track returning patients (not patient.id which is unique per visit)
      const patientVisits = new Map<string, Date[]>();
      completedPatients.forEach(patient => {
        if (patient.registeredAt) {
          // Use masterPatientId if available, otherwise fall back to contactNumber
          const patientKey = patient.masterPatientId || patient.contactNumber;
          const visits = patientVisits.get(patientKey) || [];
          visits.push(new Date(patient.registeredAt));
          patientVisits.set(patientKey, visits);
        }
      });

      const returningPatients = Array.from(patientVisits.values())
        .filter(visits => visits.length > 1).length;
      
      const retentionRate = patientVisits.size > 0 
        ? (returningPatients / patientVisits.size) * 100 
        : 0;

      const daysBetweenVisits = Array.from(patientVisits.values())
        .filter(visits => visits.length > 1)
        .flatMap(visits => {
          const sortedVisits = [...visits].sort((a, b) => a.getTime() - b.getTime());
          return sortedVisits.slice(1).map((visit, i) => 
            differenceInDays(visit, sortedVisits[i])
          );
        });

      const avgDaysBetweenVisits = daysBetweenVisits.length 
        ? daysBetweenVisits.reduce((sum, days) => sum + days, 0) / daysBetweenVisits.length 
        : 0;

      // Calculate doctor KPIs
      const doctorKPIs = doctors.map(doctor => {
        const doctorPatients = completedPatients.filter(p => p.doctorId === doctor.id);
        const totalDoctorRevenue = doctorPatients.reduce((sum, p) => sum + getPatientRevenue(p), 0);
        const avgRevenuePerPatient = doctorPatients.length 
          ? totalDoctorRevenue / doctorPatients.length 
          : 0;
        
        // Simulated patient satisfaction (in a real app, this would come from feedback)
        // Use deterministic value based on doctor ID for consistency
        const satisfactionSeed = doctor.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
        const patientSatisfaction = 70 + (satisfactionSeed % 30); // Range: 70-100
        
        // Efficiency based on average consultation time (simulated)
        // Use deterministic value based on doctor ID for consistency
        const efficiencySeed = (doctor.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) * 3) % 100;
        const efficiency = 60 + (efficiencySeed % 40); // Range: 60-100
        
        return {
          doctorId: doctor.id,
          doctorName: doctor.name,
          avgRevenuePerPatient,
          patientSatisfaction,
          efficiency
        };
      });

      // Calculate time-based analytics
      const hourMap = new Map<number, number>();
      const dayMap = new Map<string, number>();
      let totalWaitTime = 0;
      let waitTimeCount = 0;

      completedPatients.forEach(patient => {
        if (patient.registeredAt) {
          const date = new Date(patient.registeredAt);
          const hour = date.getHours();
          const day = format(date, 'EEEE');
          
          // Count patients by hour
          hourMap.set(hour, (hourMap.get(hour) || 0) + 1);
          
          // Count patients by day
          dayMap.set(day, (dayMap.get(day) || 0) + 1);
          
          // Calculate wait time in minutes
          if (patient.registeredAt && patient.completedAt) {
            const waitTime = differenceInMinutes(
              new Date(patient.completedAt),
              new Date(patient.registeredAt)
            );
            // Only count positive wait times (completed after registered)
            if (waitTime > 0) {
              totalWaitTime += waitTime;
              waitTimeCount++;
            }
          }
        }
      });

      const peakHours = Array.from(hourMap.entries())
        .map(([hour, count]) => ({ hour, count }))
        .sort((a, b) => b.count - a.count);

      const peakDays = Array.from(dayMap.entries())
        .map(([day, count]) => ({ day, count }))
        .sort((a, b) => b.count - a.count);

      const avgWaitTime = waitTimeCount ? totalWaitTime / waitTimeCount : 0;

      // Simple revenue forecasting (using last 3 months average)
      const lastThreeMonths = monthlyTrend.slice(0, 3);
      const averageRevenue = lastThreeMonths.length > 0 
        ? lastThreeMonths.reduce((sum, month) => sum + month.revenue, 0) / lastThreeMonths.length 
        : 0;
      
      const revenueForecast = Array.from({ length: 3 }, (_, i) => {
        const date = new Date();
        date.setMonth(date.getMonth() + i + 1);
        return {
          date: date.toLocaleString('default', { month: 'short', year: 'numeric' }),
          forecast: averageRevenue
        };
      });
      
      const result = { 
        totalRevenue, 
        doctorRevenue, 
        dateRevenue,
        paymentMethodRevenue,
        serviceRevenue,
        presentMonthServiceRevenue, // Add present month service revenue
        previousMonthServiceRevenue, // Add previous month service revenue
        monthlyTrend,
        revenueForecast,
        dailyRevenueData, // Add daily revenue data to the result
        dailyPatientData, // Add daily patient data to the result
        growthMetrics: {
          revenueGrowth,
          patientGrowth,
          avgRevenuePerPatient
        },
        retentionMetrics: {
          returningPatients,
          retentionRate,
          avgDaysBetweenVisits
        },
        doctorKPIs,
        timeAnalytics: {
          peakHours,
          peakDays,
          avgWaitTime
        }
      };
      
      return result;
    } catch (error) {
      console.error('Analytics: Error in getRevenueSummary:', error);
      // Return default values if there's an error
      return {
        totalRevenue: 0,
        doctorRevenue: [],
        dateRevenue: [],
        paymentMethodRevenue: [],
        serviceRevenue: [],
        presentMonthServiceRevenue: [], // Return empty array for present month service revenue on error
        previousMonthServiceRevenue: [], // Return empty array for previous month service revenue on error
        monthlyTrend: [],
        revenueForecast: [],
        dailyRevenueData: [], // Return empty array for daily data on error
        dailyPatientData: [], // Return empty array for daily patient data on error
        growthMetrics: {
          revenueGrowth: 0,
          patientGrowth: 0,
          avgRevenuePerPatient: 0
        },
        retentionMetrics: {
          returningPatients: 0,
          retentionRate: 0,
          avgDaysBetweenVisits: 0
        },
        doctorKPIs: [],
        timeAnalytics: {
          peakHours: [],
          peakDays: [],
          avgWaitTime: 0
        }
      };
    }
  },
  
  getPatientCountByDoctor: () => {
    try {
      const { patients } = usePatientsStore.getState();
      const { doctors } = useDoctorsStore.getState();
      
      const completedPatients = patients.filter(p => p.status === "completed");
      
      const result = doctors.map(doctor => {
        const count = completedPatients.filter(p => p.doctorId === doctor.id).length;
        return {
          doctorId: doctor.id,
          doctorName: doctor.name,
          count,
        };
      }).sort((a, b) => b.count - a.count);
      
      return result;
    } catch (error) {
      console.error('Analytics: Error in getPatientCountByDoctor:', error);
      return [];
    }
  },
  
  getCompletedAppointmentsByDate: (startDate, endDate) => {
    try {
      const { patients } = usePatientsStore.getState();
      const completedPatients = patients.filter(p => p.status === "completed");
      
      const filteredPatients = startDate && endDate
        ? completedPatients.filter(p => {
            if (!p.registeredAt) return false;
            const date = new Date(p.registeredAt);
            return date >= startDate && date <= endDate;
          })
        : completedPatients;
      
      const dateMap = new Map<string, number>();
      filteredPatients.forEach(patient => {
        if (patient.registeredAt) {
          const date = new Date(patient.registeredAt).toISOString().split('T')[0];
          const currentCount = dateMap.get(date) || 0;
          dateMap.set(date, currentCount + 1);
        }
      });
      
      const result = Array.from(dateMap.entries())
        .map(([date, count]) => ({ date, count }))
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      
      return result;
    } catch (error) {
      console.error('Analytics: Error in getCompletedAppointmentsByDate:', error);
      return [];
    }
  },
}));
